import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Scissors,
  CreditCard,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { apiFetch } from '@/lib/operator-api';

interface BookingDetail {
  id: string;
  status: string;
  scheduledAt: string;
  endsAt: string;
  notes: string | null;
  confirmedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  customer: { id: string; name: string | null; phone: string | null } | null;
  service: { id: string; name: string; durationMinutes: number; priceCents: number } | null;
  business: { id: string; name: string } | null;
  payment: { id: string; status: string; method: string; amountCents: number } | null;
}

const STATUS_CONFIG: Record<string, { color: string; icon: typeof CheckCircle2; label: string }> = {
  PENDING: { color: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Pending' },
  CONFIRMED: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle2, label: 'Confirmed' },
  IN_PROGRESS: { color: 'bg-purple-100 text-purple-800', icon: Loader2, label: 'In Progress' },
  COMPLETED: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Completed' },
  CANCELLED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' },
  NO_SHOW: { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: 'No Show' },
};

export const Route = createFileRoute('/_auth/operator/bookings/$id')({
  component: BookingDetailPage,
});

function BookingDetailPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchBooking = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const res = await apiFetch(`/api/operator/bookings/${id}`, { signal });
      if (signal?.aborted) return;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setBooking(json.data);
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    fetchBooking(controller.signal);
    return () => controller.abort();
  }, [fetchBooking]);

  const handleStatusChange = async (newStatus: string) => {
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/operator/bookings/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      await fetchBooking();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/operator/bookings' })}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-500" />
            <p className="text-red-700">{error || 'Booking not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[booking.status] || STATUS_CONFIG.PENDING;
  const StatusIcon = statusCfg.icon;
  const scheduledDate = new Date(booking.scheduledAt);
  const endsDate = new Date(booking.endsAt);
  const durationMin = Math.round((endsDate.getTime() - scheduledDate.getTime()) / 60_000);

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/operator/bookings' })}>
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.back')}
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('bookings.bookingDetails')}
          </h1>
        </div>
        <Badge className={`${statusCfg.color} gap-1.5 px-3 py-1.5`}>
          <StatusIcon className="h-4 w-4" />
          {statusCfg.label}
        </Badge>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Booking Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Appointment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {scheduledDate.toLocaleDateString('pt-BR', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-medium">
                    {scheduledDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    {' - '}
                    {endsDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    <span className="text-muted-foreground ml-1">({durationMin} min)</span>
                  </p>
                </div>
              </div>

              {booking.service && (
                <>
                  <Separator />
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Scissors className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{booking.service.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {booking.service.durationMinutes} min
                      </p>
                    </div>
                    <p className="font-semibold text-lg">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                        booking.service.priceCents / 100
                      )}
                    </p>
                  </div>
                </>
              )}

              {booking.notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{booking.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Status Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <TimelineItem
                  label="Created"
                  time={booking.createdAt}
                  active={true}
                />
                {booking.confirmedAt && (
                  <TimelineItem label="Confirmed" time={booking.confirmedAt} active={true} />
                )}
                {booking.cancelledAt && (
                  <TimelineItem label="Cancelled" time={booking.cancelledAt} active={true} isError />
                )}
                {booking.status === 'COMPLETED' && (
                  <TimelineItem label="Completed" time={booking.endsAt} active={true} />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Customer & Actions */}
        <div className="space-y-6">
          {/* Customer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent>
              {booking.customer ? (
                <div className="space-y-2">
                  <p className="font-medium">{booking.customer.name || 'Unknown'}</p>
                  {booking.customer.phone && (
                    <p className="text-sm text-muted-foreground">{booking.customer.phone}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No customer info</p>
              )}
            </CardContent>
          </Card>

          {/* Payment */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Payment
              </CardTitle>
            </CardHeader>
            <CardContent>
              {booking.payment ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge variant="outline">{booking.payment.status}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Method</span>
                    <span className="text-sm font-medium">{booking.payment.method}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Amount</span>
                    <span className="font-semibold">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                        booking.payment.amountCents / 100
                      )}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No payment recorded</p>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {booking.status === 'PENDING' && (
                <Button
                  className="w-full"
                  onClick={() => handleStatusChange('CONFIRMED')}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Confirm Booking
                </Button>
              )}
              {booking.status === 'CONFIRMED' && (
                <Button
                  className="w-full"
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  disabled={actionLoading}
                >
                  Start Service
                </Button>
              )}
              {booking.status === 'IN_PROGRESS' && (
                <Button
                  className="w-full"
                  onClick={() => handleStatusChange('COMPLETED')}
                  disabled={actionLoading}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Complete
                </Button>
              )}
              {!['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(booking.status) && (
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => handleStatusChange('CANCELLED')}
                  disabled={actionLoading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Booking
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function TimelineItem({ label, time, active, isError }: {
  label: string; time: string; active: boolean; isError?: boolean;
}) {
  const dateStr = new Date(time).toLocaleString('pt-BR', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="flex items-center gap-3">
      <div className={`w-3 h-3 rounded-full ${
        isError ? 'bg-red-500' : active ? 'bg-primary' : 'bg-muted'
      }`} />
      <div className="flex-1">
        <p className={`text-sm font-medium ${isError ? 'text-red-700' : ''}`}>{label}</p>
        <p className="text-xs text-muted-foreground">{dateStr}</p>
      </div>
    </div>
  );
}
